<template>
  <div class="trix-content rich-text-content" v-html="processedContent"></div>
</template>

<script>
import { detectProvider, generateEmbedData } from './rich_text_editor/useEmbeds';

export default {
  name: 'RichTextContent',

  props: {
    content: {
      type: String,
      default: ''
    },
    // Whether to process embeds in the content
    enableEmbeds: {
      type: Boolean,
      default: true
    },
    // Whether to add nofollow to all links
    addNofollow: {
      type: Boolean,
      default: true
    },
    // Whether to open links in new tab
    targetBlank: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      processedContent: ''
    };
  },

  watch: {
    content: {
      immediate: true,
      handler(newContent) {
        this.processContent(newContent);
      }
    }
  },

  methods: {
    async processContent(content) {
      if (!content) {
        this.processedContent = '';
        return;
      }

      let processed = content;

      // Translate the editor's gallery-grid marker into the class the
      // stylesheet keys off (mirrors TrixGalleryGrid on the server)
      processed = this.markGridGalleries(processed);

      // Process links
      if (this.addNofollow || this.targetBlank) {
        processed = this.processLinks(processed);
      }

      // Process embeds
      if (this.enableEmbeds) {
        processed = await this.processEmbeds(processed);
      }

      this.processedContent = processed;
    },

    markGridGalleries(content) {
      if (!content.includes('gallery_layout')) return content;

      const div = document.createElement('div');
      div.innerHTML = content;

      div.querySelectorAll('div.attachment-gallery').forEach(gallery => {
        const optedIn = [...gallery.querySelectorAll('figure')].some(figure =>
          (figure.getAttribute('data-trix-attachment') || '').includes('"gallery_layout":"grid"')
        );
        if (optedIn) gallery.classList.add('attachment-gallery--grid');
      });

      return div.innerHTML;
    },

    processLinks(content) {
      // Create a temporary div to parse HTML
      const div = document.createElement('div');
      div.innerHTML = content;

      // Find all links
      const links = div.querySelectorAll('a');
      links.forEach(link => {
        // Skip internal links and mention spans
        if (link.classList.contains('mention')) return;

        const href = link.getAttribute('href');
        if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;

        if (this.targetBlank) {
          link.setAttribute('target', '_blank');
        }

        if (this.addNofollow) {
          const rel = link.getAttribute('rel') || '';
          const relParts = rel.split(' ').filter(r => r);

          if (!relParts.includes('nofollow')) {
            relParts.push('nofollow');
          }
          if (!relParts.includes('noopener')) {
            relParts.push('noopener');
          }

          link.setAttribute('rel', relParts.join(' '));
        }
      });

      return div.innerHTML;
    },

    async processEmbeds(content) {
      // Find URLs that could be embeds
      // Look for standalone URLs or URLs in attachment elements
      const urlPattern = /https?:\/\/[^\s<>"]+/g;
      const urls = content.match(urlPattern) || [];

      for (const url of urls) {
        const detected = detectProvider(url);
        if (detected) {
          try {
            const embedData = await generateEmbedData(url);
            if (embedData.success) {
              // Replace URL with embed preview
              const embedHtml = this.generateEmbedHTML(embedData);
              // Only replace standalone URLs, not those in href attributes
              const standaloneUrlPattern = new RegExp(
                `(?<!href=["'])${this.escapeRegex(url)}(?!["'])`,
                'g'
              );
              content = content.replace(standaloneUrlPattern, embedHtml);
            }
          } catch (e) {
            // Keep original URL if embed fails
            console.warn('Failed to process embed:', url, e);
          }
        }
      }

      return content;
    },

    generateEmbedHTML(embedData) {
      const { provider, providerName, type, embedUrl, originalUrl, title, thumbnail } = embedData;

      if (type === 'video') {
        return `
          <div class="embed-preview embed-preview--video" data-provider="${provider}">
            <div class="embed-preview__video">
              <iframe
                src="${embedUrl}"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                loading="lazy"
              ></iframe>
            </div>
          </div>
        `;
      }

      if (type === 'iframe') {
        return `
          <div class="embed-preview embed-preview--iframe" data-provider="${provider}" style="aspect-ratio: ${embedData.aspectRatio || '16/9'}">
            <iframe
              src="${embedUrl}"
              frameborder="0"
              allowfullscreen
              loading="lazy"
            ></iframe>
          </div>
        `;
      }

      // Card fallback
      return `
        <div class="embed-preview embed-preview--card" data-provider="${provider}">
          <a href="${originalUrl}" target="_blank" rel="nofollow noopener" class="embed-preview__link">
            ${thumbnail ? `<img src="${thumbnail}" class="embed-preview__thumbnail" alt="" loading="lazy" />` : ''}
            <div class="embed-preview__content">
              <span class="embed-preview__title">${this.escapeHtml(title || originalUrl)}</span>
              <span class="embed-preview__provider">${this.escapeHtml(providerName)}</span>
            </div>
          </a>
        </div>
      `;
    },

    escapeRegex(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }
};
</script>

<style scoped>
.rich-text-content {
  word-wrap: break-word;
  overflow-wrap: break-word;
}
</style>
